/**
 * useQChat Hook (v4.0 - Chat History + Conversation Persistence)
 * Custom hook for managing Q AI chat state and SSE streaming
 *
 * v4.0 Updates (Task 4.0-4.7):
 * - localStorage persistence for conversationId (survives refresh)
 * - Conversation history list (getUserConversations API)
 * - Load past conversations from history panel
 * - Delete conversations
 * - Auto-restore conversation on page refresh
 *
 * v3.0 Updates (Task 2.3/3.3):
 * - Store conversationId from API response
 * - Pass conversationId in subsequent requests
 * - Track response source (LIVE/CACHED/FALLBACK)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  getBalance,
  askQAI,
  getActionCost,
  getUserConversations,
  getConversationHistory,
  deleteConversation
} from '../api/qaiApi';
import { FEATURE_FLAGS } from '../config/featureFlags';

// ═══════════════════════════════════════════════════════════════════════════
// DEBUG FLAG - Set to false for production
// ═══════════════════════════════════════════════════════════════════════════
const DEBUG = process.env.NODE_ENV === 'development';
const log = (...args) => DEBUG && console.log(...args);
const warn = (...args) => DEBUG && console.warn(...args);
const error = (...args) => console.error(...args); // Always log errors

// ═══════════════════════════════════════════════════════════════════════════
// LOCALSTORAGE KEYS - Task 4.1: Persist conversationId across refresh
// ═══════════════════════════════════════════════════════════════════════════
const STORAGE_KEY_CONVERSATION_ID = 'qai_current_conversation_id';

// ═══════════════════════════════════════════════════════════════════════════
// WELCOME MESSAGE TEMPLATE (Feature Flagged)
// ═══════════════════════════════════════════════════════════════════════════

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

/**
 * PREMIUM Welcome Message - Centered card with Q introduction
 * Feature flag: PREMIUM_WELCOME
 */
const createPremiumWelcomeMessage = () => ({
  id: 'welcome',
  role: 'assistant',
  content: `I'm **Q**, your AI trading assistant.

Ask me about signals, analyze your trades, or get coaching tips.`,
  showActionChips: true,
  isPremiumWelcome: true, // Flag for special rendering
  timestamp: new Date().toISOString()
});

/**
 * ORIGINAL Welcome Message - Standard with action chips
 */
const createOriginalWelcomeMessage = () => ({
  id: 'welcome',
  role: 'assistant',
  content: `I'm **Q**, your AI trading coach.

Here's what I can help with:`,
  showActionChips: true,
  timestamp: new Date().toISOString()
});

/**
 * Get welcome message based on feature flag
 */
const createWelcomeMessage = () => {
  return FEATURE_FLAGS.PREMIUM_WELCOME
    ? createPremiumWelcomeMessage()
    : createOriginalWelcomeMessage();
};

/**
 * Parse AI response for analysis data to display on the board
 * Detects performance analysis, signals, or custom data patterns
 */
const parseAnalysisFromResponse = (content, userQuery) => {
  if (!content || content.length < 50) return null;

  const lowerContent = content.toLowerCase();
  const lowerQuery = userQuery?.toLowerCase() || '';

  // Detect performance analysis keywords
  const isPerformance = lowerQuery.includes('performance') ||
    lowerQuery.includes('analyze') ||
    lowerQuery.includes('review') ||
    lowerContent.includes('win rate') ||
    lowerContent.includes('profit factor') ||
    lowerContent.includes('drawdown');

  // Detect signals keywords
  const isSignals = lowerQuery.includes('signal') ||
    lowerQuery.includes('entry') ||
    lowerQuery.includes('trade idea') ||
    lowerContent.includes('buy signal') ||
    lowerContent.includes('sell signal');

  // Detect trades/history keywords (E4)
  const isTrades = lowerQuery.includes('trade history') ||
    lowerQuery.includes('my trades') ||
    lowerQuery.includes('recent trades') ||
    lowerQuery.includes('show trades') ||
    lowerQuery.includes('trading history') ||
    lowerContent.includes('your trades') ||
    lowerContent.includes('trade history');

  if (isTrades) {
    return {
      type: 'trades',
      title: 'Your Trade History',
      subtitle: 'Synced from MT5 via QBridge',
      trades: extractTradesFromContent(content),
      summary: extractTradeSummary(content)
    };
  }

  if (isPerformance) {
    // Extract performance metrics (simplified parsing)
    return {
      type: 'performance',
      title: 'Your Trading Performance',
      subtitle: 'AI-Generated Analysis',
      stats: extractStatsFromContent(content),
      strengths: extractBulletPoints(content, ['strength', 'well', 'good', 'strong']),
      improvements: extractBulletPoints(content, ['improve', 'consider', 'reduce', 'watch']),
      potential: extractPotential(content)
    };
  }

  if (isSignals) {
    return {
      type: 'signals',
      title: 'Market Signals',
      signals: extractSignalsFromContent(content)
    };
  }

  // Generic analysis - display as custom
  if (content.length > 200) {
    return {
      type: 'custom',
      title: 'Q Analysis',
      content: content.substring(0, 500) + (content.length > 500 ? '...' : '')
    };
  }

  return null;
};

// Helper: Extract stats from content
const extractStatsFromContent = (content) => {
  const stats = [];

  // Try to find percentage values
  const winRateMatch = content.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:win|winning)/i);
  if (winRateMatch) {
    stats.push({ label: 'Win Rate', value: `${winRateMatch[1]}%`, color: parseFloat(winRateMatch[1]) >= 50 ? 'green' : 'red' });
  }

  const profitMatch = content.match(/(?:profit|gain|return)[^\d]*(\d+(?:\.\d+)?)\s*%?/i);
  if (profitMatch) {
    stats.push({ label: 'Total Return', value: `+${profitMatch[1]}%`, color: 'green' });
  }

  const tradesMatch = content.match(/(\d+)\s*(?:trades?|positions?)/i);
  if (tradesMatch) {
    stats.push({ label: 'Total Trades', value: tradesMatch[1], color: 'default' });
  }

  // Fallback if no stats found
  if (stats.length === 0) {
    stats.push({ label: 'Analysis', value: 'Complete', color: 'green' });
  }

  return stats;
};

// Helper: Extract bullet points
const extractBulletPoints = (content, keywords) => {
  const sentences = content.split(/[.!?]+/);
  const matches = sentences
    .filter(s => keywords.some(k => s.toLowerCase().includes(k)))
    .map(s => s.trim())
    .filter(s => s.length > 10 && s.length < 200)
    .slice(0, 3);

  return matches.length > 0 ? matches : null;
};

// Helper: Extract potential impact
const extractPotential = (content) => {
  const potentialMatch = content.match(/(?:potential|could|may|might)[^.]*(\d+(?:\.\d+)?%[^.]*)/i);
  return potentialMatch ? potentialMatch[1].trim() : null;
};

// Helper: Extract signals from content
const extractSignalsFromContent = (content) => {
  const signals = [];
  const pairs = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD', 'EUR/GBP', 'EURUSD', 'GBPUSD', 'USDJPY'];

  pairs.forEach(pair => {
    const pairRegex = new RegExp(pair.replace('/', '/?'), 'i');
    if (pairRegex.test(content)) {
      const isBuy = content.toLowerCase().includes('buy') || content.toLowerCase().includes('long');
      const isSell = content.toLowerCase().includes('sell') || content.toLowerCase().includes('short');

      signals.push({
        pair: pair.includes('/') ? pair : pair.slice(0, 3) + '/' + pair.slice(3),
        direction: isBuy ? 'BUY' : isSell ? 'SELL' : 'WAIT',
        entry: 'Market',
        score: Math.floor(Math.random() * 20) + 70 // Placeholder score
      });
    }
  });

  return signals.length > 0 ? signals : null;
};

// Helper: Extract trades from content (E4)
const extractTradesFromContent = (content) => {
  const trades = [];
  const pairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'EURGBP', 'XAUUSD', 'BTCUSD'];

  // Try to find trade mentions in content
  pairs.forEach(pair => {
    const pairRegex = new RegExp(`${pair}[^\\d]*(buy|sell|long|short)[^\\d]*([\\d.]+)`, 'gi');
    const matches = content.matchAll(pairRegex);

    for (const match of matches) {
      const type = match[1].toLowerCase();
      trades.push({
        id: `${pair}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        symbol: pair.slice(0, 3) + '/' + pair.slice(3),
        type: type === 'buy' || type === 'long' ? 'BUY' : 'SELL',
        volume: parseFloat(match[2]) || 0.1,
        profit: (Math.random() * 200 - 100), // Placeholder
        closeTime: new Date().toISOString()
      });
    }
  });

  return trades;
};

// Helper: Extract trade summary from content (E4)
const extractTradeSummary = (content) => {
  const summary = {};

  // Try to extract total trades
  const tradesMatch = content.match(/(\d+)\s*(?:total\s*)?trades?/i);
  if (tradesMatch) {
    summary.totalTrades = parseInt(tradesMatch[1], 10);
  }

  // Try to extract win rate
  const winRateMatch = content.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:win|winning)\s*rate/i);
  if (winRateMatch) {
    summary.winRate = parseFloat(winRateMatch[1]);
  }

  // Try to extract total P&L
  const pnlMatch = content.match(/(?:total|net)\s*(?:p&?l|profit|return)[:\s]*\$?([-\d,.]+)/i);
  if (pnlMatch) {
    summary.totalPnL = parseFloat(pnlMatch[1].replace(/,/g, ''));
  }

  return Object.keys(summary).length > 0 ? summary : null;
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * useQChat Hook (Enhanced for UI-6, UI-7, UI-8, Symbol Context)
 * @param {string} token - JWT token for API authentication
 * @param {string} userName - User's display name for personalized greeting
 * @param {function} onAnalysisData - Callback when AI provides analysis data for the board
 * @param {string} selectedSymbol - Currently selected symbol for context-aware responses
 * @returns {object} Chat state and actions
 */
const useQChat = (token, userName = 'Trader', onAnalysisData = null, selectedSymbol = null) => {
  // State
  const [messages, setMessages] = useState([createWelcomeMessage()]);
  const [balance, setBalance] = useState(null); // null = loading, 0 = no tokens
  const [isLoading, setIsLoading] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [error, setError] = useState(null);

  // Task 2.3/4.1: Conversation ID for multi-turn context (persisted in localStorage)
  const [conversationId, setConversationId] = useState(() => {
    // Task 4.1: Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(STORAGE_KEY_CONVERSATION_ID);
      if (stored) {
        log('[useQChat] Restored conversationId from localStorage:', stored);
        return stored;
      }
    } catch (err) {
      warn('[useQChat] Failed to read conversationId from localStorage:', err);
    }
    return null;
  });

  // Task 3.3: Last response source (LIVE/CACHED/FALLBACK)
  const [lastResponseSource, setLastResponseSource] = useState(null);

  // Task 4.6: Conversation history state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [userId, setUserId] = useState(null);  // From getBalance response

  // UI-6: Progress state
  const [progressState, setProgressState] = useState({
    currentStep: null,
    progress: 0,
    message: ''
  });

  // UI-8: Warnings state
  const [warnings, setWarnings] = useState([]);

  // Ref for abort controller
  const abortControllerRef = useRef(null);

  // Task 4.1: Persist conversationId to localStorage whenever it changes
  useEffect(() => {
    try {
      if (conversationId) {
        localStorage.setItem(STORAGE_KEY_CONVERSATION_ID, conversationId);
        log('[useQChat] Saved conversationId to localStorage:', conversationId);
      } else {
        localStorage.removeItem(STORAGE_KEY_CONVERSATION_ID);
        log('[useQChat] Cleared conversationId from localStorage');
      }
    } catch (err) {
      warn('[useQChat] Failed to persist conversationId to localStorage:', err);
    }
  }, [conversationId]);

  // Ref to track if conversation has been restored
  const conversationRestoredRef = useRef(false);

  // Task 4.7: Restore conversation on page refresh
  // Only runs once when token becomes available and there's a stored conversationId
  // Note: We capture conversationId via closure, not dependency, to prevent re-runs
  // when setConversationId(null) is called on error
  useEffect(() => {
    const restoreConversation = async () => {
      // Check if we already restored or have no token
      if (conversationRestoredRef.current || !token) {
        return;
      }

      // Get the stored conversation ID (may be from initial state or localStorage)
      const storedId = localStorage.getItem(STORAGE_KEY_CONVERSATION_ID);
      if (!storedId) {
        return;
      }

      // Mark as restored to prevent duplicate calls
      conversationRestoredRef.current = true;

      log('[useQChat] Restoring conversation from localStorage:', storedId);

      try {
        const result = await getConversationHistory(token, storedId);
        log('[useQChat] Conversation restored:', result.messages?.length || 0, 'messages');

        if (result.messages && result.messages.length > 0) {
          // Replace messages with loaded history
          const welcomeMessage = createWelcomeMessage();
          const loadedMessages = result.messages.map(msg => ({
            ...msg,
            id: msg.id || `${msg.role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
          }));

          setMessages([welcomeMessage, ...loadedMessages]);
          // Ensure conversationId state matches
          setConversationId(storedId);
          log('[useQChat] Conversation restored successfully');
        } else {
          // No messages found, clear the stored conversationId
          log('[useQChat] No messages found for stored conversation, clearing');
          setConversationId(null);
        }
      } catch (err) {
        console.error('[useQChat] Failed to restore conversation:', err);
        // Clear invalid conversationId
        setConversationId(null);
      }
    };

    restoreConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // Only depend on token - userName is stable, conversationId read from localStorage

  // Ref to store the callback
  const onAnalysisDataRef = useRef(onAnalysisData);
  onAnalysisDataRef.current = onAnalysisData;

  // ─────────────────────────────────────────────────────────────────────────
  // Refresh balance from API (Task 4.6: also extracts userId for history API)
  // ─────────────────────────────────────────────────────────────────────────
  const refreshBalance = useCallback(async () => {
    if (!token) {
      log('[useQChat] refreshBalance skipped - no token');
      return null;
    }

    try {
      log('[useQChat] Fetching balance...');
      const data = await getBalance(token);
      log('[useQChat] Balance fetched:', data.balance, 'userId:', data.userId);
      setBalance(data.balance);
      // Task 4.6: Store userId for conversation history API
      if (data.userId) {
        setUserId(data.userId);
      }
      return data;
    } catch (err) {
      console.error('[useQChat] Failed to refresh balance:', err);
      // CRITICAL FIX: Keep balance as null on error to show "Loading..." state
      // Setting to 0 incorrectly implies user has no tokens
      // The UI will show appropriate error/retry messaging
      // Only set to 0 if we KNOW user has zero tokens from API
      return null;
    }
  }, [token]);

  // ─────────────────────────────────────────────────────────────────────────
  // Send message with SSE streaming (Enhanced for UI-6, UI-7, UI-8)
  // ─────────────────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (content, actionType = 'chat') => {
    if (!token || !content.trim() || isLoading) return;

    // CRITICAL FIX: Check balance BEFORE allowing message send
    // balance === null means still loading, balance === 0 means no tokens
    if (balance === null) {
      log('[useQChat] sendMessage blocked - balance still loading');
      setError('Loading balance... please wait');
      return;
    }
    if (balance <= 0) {
      log('[useQChat] sendMessage blocked - insufficient tokens:', balance);
      setShowPricingModal(true);
      return;
    }

    setError(null);
    setIsLoading(true);
    // Reset progress and warnings for new request
    setProgressState({ currentStep: null, progress: 0, message: 'Starting...' });
    setWarnings([]);

    // Add user message immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    // Create placeholder for assistant response
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Start SSE streaming with enhanced callbacks (Task 2.3: pass conversationId, Symbol Context: pass selectedSymbol)
    log('[useQChat] sendMessage starting:', {
      message: content.substring(0, 50) + (content.length > 50 ? '...' : ''),
      actionType,
      selectedSymbol: selectedSymbol || '(none - will use all symbols)',
      currentConversationId: conversationId || '(none - will start new conversation)',
      hasConversationId: !!conversationId
    });

    abortControllerRef.current = askQAI(
      token,
      content,
      actionType,
      selectedSymbol,
      // onToken - append each token
      (tokenText) => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: msg.content + tokenText }
              : msg
          )
        );
      },
      // onDone - update balance, conversationId, source and mark complete
      (doneData) => {
        setIsLoading(false);
        // Reset progress state
        setProgressState({ currentStep: null, progress: 100, message: '' });

        const newBalance = typeof doneData === 'object' ? doneData?.newBalance : doneData;
        if (newBalance !== null && newBalance !== undefined) {
          setBalance(newBalance);
        }

        // Task 2.3: Store conversationId for subsequent requests
        log('[useQChat] onDone received:', {
          conversationId: doneData?.conversationId,
          hasConversationId: !!doneData?.conversationId,
          source: doneData?.source,
          newBalance: doneData?.newBalance
        });
        if (doneData?.conversationId) {
          log('[useQChat] Storing conversationId:', doneData.conversationId);
          setConversationId(doneData.conversationId);
        } else {
          warn('[useQChat] WARNING: No conversationId in done event! Backend may not be returning it.');
        }

        // Task 3.3: Store response source
        const responseSource = doneData?.source || 'LIVE';
        setLastResponseSource(responseSource);

        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  isStreaming: false,
                  warnings: warnings.length > 0 ? [...warnings] : undefined,
                  source: responseSource,  // Task 3.3: attach source to message
                  conversationId: doneData?.conversationId  // Task 2.3: attach conversationId
                }
              : msg
          );

          // Parse analysis data from the completed response
          const completedMessage = updatedMessages.find(m => m.id === assistantMessageId);
          if (completedMessage && onAnalysisDataRef.current) {
            const analysisData = parseAnalysisFromResponse(completedMessage.content, content);
            if (analysisData) {
              onAnalysisDataRef.current(analysisData);
            }
          }

          return updatedMessages;
        });
        abortControllerRef.current = null;
      },
      // onError - handle errors
      (err) => {
        setIsLoading(false);
        setProgressState({ currentStep: null, progress: 0, message: '' });
        abortControllerRef.current = null;

        if (err.code === 402) {
          // Insufficient tokens - show pricing modal
          setShowPricingModal(true);
          // Remove the empty assistant message
          setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
        } else {
          setError(err.message);
          // Update assistant message with error
          setMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: 'Sorry, I encountered an error. Please try again.',
                    isStreaming: false,
                    isError: true
                  }
                : msg
            )
          );
        }
      },
      // Extended callbacks for UI-6, UI-7, UI-8
      {
        // UI-6: Status updates
        onStatus: (status) => {
          setProgressState({
            currentStep: status.step,
            progress: status.progress,
            message: status.message
          });
        },
        // UI-8: Partial failure warnings
        onWarning: (warning) => {
          setWarnings(prev => [...prev, warning]);
        },
        // UI-6: Ready to generate
        onReady: () => {
          setProgressState(prev => ({
            ...prev,
            currentStep: 'generating',
            progress: 85,
            message: 'Q is thinking...'
          }));
        }
      },
      conversationId  // Task 2.3: Pass conversationId for multi-turn context
    );
  }, [token, isLoading, balance, warnings, conversationId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Handle action card click (legacy)
  // ─────────────────────────────────────────────────────────────────────────
  const handleActionCard = useCallback((actionId) => {
    // Legacy action cards - kept for backward compatibility
    return { focusInput: true };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Handle action chip click (new mockup design)
  // ─────────────────────────────────────────────────────────────────────────
  const handleActionChip = useCallback((action) => {
    // action comes from QActionChips with { id, prompt, label, ... }
    if (!action?.prompt) return null;

    sendMessage(action.prompt, 'chat');
    return { sent: true };
  }, [sendMessage]);

  // ─────────────────────────────────────────────────────────────────────────
  // Cancel current stream
  // ─────────────────────────────────────────────────────────────────────────
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);

      // Mark any streaming messages as complete
      setMessages(prev =>
        prev.map(msg =>
          msg.isStreaming
            ? { ...msg, isStreaming: false, content: msg.content + ' [cancelled]' }
            : msg
        )
      );
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Clear messages (reset to welcome) - Task 2.3: also reset conversationId
  // ─────────────────────────────────────────────────────────────────────────
  const clearMessages = useCallback(() => {
    setMessages([createWelcomeMessage()]);
    setError(null);
    setProgressState({ currentStep: null, progress: 0, message: '' });
    setWarnings([]);
    setConversationId(null);  // Task 2.3: Reset conversation on clear
    setLastResponseSource(null);  // Task 3.3: Reset source
  }, []); // No dependencies - createWelcomeMessage is stable

  // ─────────────────────────────────────────────────────────────────────────
  // Task 4.6: Refresh conversation history list
  // ─────────────────────────────────────────────────────────────────────────
  const refreshConversationHistory = useCallback(async () => {
    if (!token || !userId) {
      log('[useQChat] refreshConversationHistory skipped - no token or userId');
      return;
    }

    setIsLoadingHistory(true);
    try {
      log('[useQChat] Fetching conversation history for userId:', userId);
      const result = await getUserConversations(token, userId, { limit: 20 });
      log('[useQChat] Conversation history fetched:', result.conversations?.length || 0, 'conversations');
      setConversationHistory(result.conversations || []);
    } catch (err) {
      console.error('[useQChat] Failed to fetch conversation history:', err);
      setConversationHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [token, userId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Task 4.6: Load a specific conversation from history
  // ─────────────────────────────────────────────────────────────────────────
  const loadConversation = useCallback(async (targetConversationId) => {
    log('[useQChat] loadConversation called with:', targetConversationId);

    if (!token || !targetConversationId) {
      log('[useQChat] loadConversation skipped - no token or conversationId', { token: !!token, targetConversationId });
      return;
    }

    // Don't reload current conversation
    if (targetConversationId === conversationId) {
      log('[useQChat] loadConversation skipped - already loaded');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      log('[useQChat] Loading conversation:', targetConversationId);
      const result = await getConversationHistory(token, targetConversationId);
      log('[useQChat] Conversation loaded - full result:', JSON.stringify(result, null, 2));
      log('[useQChat] Messages count:', result.messages?.length || 0);

      // Set conversation ID first (this will persist to localStorage)
      setConversationId(targetConversationId);

      // Replace messages with loaded history
      // Keep welcome message at start, then add loaded messages
      const welcomeMessage = createWelcomeMessage();
      const loadedMessages = (result.messages || []).map(msg => {
        log('[useQChat] Mapping message:', msg);
        return {
          ...msg,
          id: msg.id || `${msg.role}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
      });

      log('[useQChat] Final messages array length:', loadedMessages.length + 1);
      setMessages([welcomeMessage, ...loadedMessages]);
      setWarnings([]);
      setLastResponseSource(null);
    } catch (err) {
      console.error('[useQChat] Failed to load conversation:', err);
      setError(`Failed to load conversation: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [token, conversationId]); // userName removed - not used in function

  // ─────────────────────────────────────────────────────────────────────────
  // Task 4.6: Delete a conversation from history
  // ─────────────────────────────────────────────────────────────────────────
  const handleDeleteConversation = useCallback(async (targetConversationId) => {
    if (!token || !targetConversationId) {
      log('[useQChat] handleDeleteConversation skipped - no token or conversationId');
      return;
    }

    try {
      log('[useQChat] Deleting conversation:', targetConversationId);
      await deleteConversation(token, targetConversationId);

      // Remove from local history list
      setConversationHistory(prev =>
        prev.filter(conv => conv.conversationId !== targetConversationId)
      );

      // If we deleted the current conversation, start fresh
      if (targetConversationId === conversationId) {
        clearMessages();
      }
    } catch (err) {
      console.error('[useQChat] Failed to delete conversation:', err);
      setError(`Failed to delete conversation: ${err.message}`);
    }
  }, [token, conversationId, clearMessages]);

  // ─────────────────────────────────────────────────────────────────────────
  // Open pricing modal (for external "Buy Tokens" buttons)
  // ─────────────────────────────────────────────────────────────────────────
  const openPricingModal = useCallback(() => {
    setShowPricingModal(true);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Close pricing modal
  // ─────────────────────────────────────────────────────────────────────────
  const closePricingModal = useCallback(() => {
    setShowPricingModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Get estimated cost for action
  // ─────────────────────────────────────────────────────────────────────────
  const getEstimatedCost = useCallback((actionType = 'chat') => {
    return getActionCost(actionType);
  }, []);

  return {
    // State
    messages,
    balance,
    isLoading,
    showPricingModal,
    error,

    // Task 2.3: Conversation state
    conversationId,
    hasConversation: !!conversationId,

    // Task 3.3: Response source state
    lastResponseSource,

    // Task 4.6: Conversation history state
    conversationHistory,
    isLoadingHistory,

    // UI-6: Progress state
    progressState,

    // UI-8: Warnings state
    warnings,

    // Actions
    sendMessage,
    handleActionCard,
    handleActionChip,
    refreshBalance,
    cancelStream,
    clearMessages,
    openPricingModal,
    closePricingModal,
    getEstimatedCost,

    // Task 4.6: Conversation history actions
    refreshConversationHistory,
    loadConversation,
    deleteConversation: handleDeleteConversation,

    // Helpers
    isLowBalance: balance !== null && balance <= 15,
    canSend: !isLoading && balance !== null && balance > 0
  };
};

export default useQChat;
